/**
 * Brand Registry - Centralized brand configuration
 * 
 * This file maps brand IDs to their configurations.
 * Adding new clients only requires adding an entry here.
 */

import { udarasBrandConfig } from './udaras/brand-config';
import { citizensInfoBrandConfig } from './citizensInfo/brand-config';

// Brand registry - maps brand ID to configuration
export const brandRegistry = {
  'citizensInfo': citizensInfoBrandConfig,
  'udaras': udarasBrandConfig,
  // Future clients can be added here:
  // 'client3': client3BrandConfig,
  // 'client4': client4BrandConfig,
  // 'client20': client20BrandConfig,
} as const;

export type BrandId = keyof typeof brandRegistry;

/**
 * Get brand configuration by ID
 * @param brandId - The brand identifier (required)
 * @returns Brand configuration object
 * @throws Error if brandId is missing or invalid
 */
export function getBrandConfig(brandId: string | undefined): typeof brandRegistry[BrandId] {
  if (!brandId) {
    throw new Error('Brand ID is required. Set VITE_BRAND_ID environment variable.');
  }
  
  const config = brandRegistry[brandId as BrandId];
  if (!config) {
    const availableBrands = getAvailableBrands().join(', ');
    throw new Error(`Brand '${brandId}' not found. Available brands: ${availableBrands}`);
  }
  return config;
}

/**
 * Get all available brand IDs
 * @returns Array of available brand IDs
 */
export function getAvailableBrands(): BrandId[] {
  return Object.keys(brandRegistry) as BrandId[];
}

/**
 * Check if a brand ID is valid
 * @param brandId - The brand identifier to check
 * @returns True if brand exists
 */
export function isValidBrand(brandId: string): brandId is BrandId {
  return brandId in brandRegistry;
}
